//
import { Button, useTheme } from '@mui/material';
// components
import NavSection from '../../components/NavSection';
import { Scrollbar } from '../../components/Scrollbar';
// hooks
import { useCollapseDrawer } from '../../hooks/useCollapseDrawer';
import { useSettings } from '../../hooks/useSettings';
import { IMessage, useStores } from 'src/models';
// material
import { Box, Drawer, Stack, Typography, Link, Avatar } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { adminSidebarConfig, noAuthSidebarConfig, authSidebarConfig } from './SidebarConfig';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useSnackbar } from 'notistack';
import { toJS } from 'mobx';
import { CallApiToStore } from 'src/utils/common';
import MainLogo from 'src/assets/images/hypowave-logo.svg';
import CModalForm from 'src/components/custom/CModalForm';
import { pxToRem } from 'src/theme/typography';
import { browserName } from 'react-device-detect';
// import { CustomAvatar } from 'components/custom-avatar';
// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;
const RootStyle = styled('div')(({ theme }) => ({
  flexShrink: 0,
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.complex,
  }),
}));
const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

// ------------------------------------------------------₩----------------

export default observer(function DashboardSidebar() {
  const { user } = useAuthContext();
  const theme = useTheme();
  let { language } = useSettings();
  const { pathname } = useLocation();
  const { messageStore, authInfoStore, loadingStore, medicalDeviceInfoStore } = useStores();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();
  const { logout } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const { REACT_APP_MANUAL_URL } = process.env;

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [menu, setMenu] = useState<any>([]);

  const getAuthInfo = () => {
    CallApiToStore(authInfoStore.getAuthInfo(user?.userSid), 'api', loadingStore).then(() => {
      if (
        authInfoStore.authInfo.authType?.value! === '관리자' ||
        authInfoStore.authInfo.authType?.value! === '슈퍼관리자'
      ) {
        setMenu(adminSidebarConfig());
        authInfoStore.setProp('authYn', true);
      } else if (authInfoStore.authInfo.authYn === 'Y' && user?.acntSttsCd.value === '승인완료') {
        // 권한 O 사용자
        setMenu(authSidebarConfig());
        authInfoStore.setProp('authYn', true);
      } else {
        authInfoStore.setProp('authYn', false);
        setMenu(noAuthSidebarConfig());
      }
    });
  };

  const getMedicalDeviceInfo = () => {
    CallApiToStore(medicalDeviceInfoStore.get(), 'api', loadingStore).then(() => {
      setModalOpen(true);
    });
  };

  useEffect(() => {
    getAuthInfo();
  }, [language, user, pathname]);

  // 에러메시지 처리
  useEffect(() => {
    if (messageStore.message.content) {
      enqueueSnackbar(messageStore.message.content, {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: messageStore.message.isError ? 'error' : 'success',
        autoHideDuration: 3000,
      });
      messageStore.setProps({ message: {} as IMessage });
    }
  }, [messageStore.message, messageStore.message.content, messageStore.message.isError]);

  /** 사용자매뉴얼 다운로드 */
  const downloadManual =useCallback(() => {
    let url = REACT_APP_MANUAL_URL + 'HypoWave_사용자매뉴얼.pdf';

    if (browserName === 'Safari') {
      enqueueSnackbar('매뉴얼 다운로드는 다른 브라우저를 이용해주시길 바랍니다.', {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'error',
        autoHideDuration: 5000,
      });
    } else {
      window.open(url);
    }
  },[REACT_APP_MANUAL_URL, enqueueSnackbar]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center',
          }),
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box component={'img'} src={MainLogo}></Box>
        </Box>

        <AccountStyle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Avatar alt="My Avatar" sx={{}} />
          <Box sx={{ ml: -4, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              {user?.userNm ?? ''}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {user?.loginId ?? ''}
            </Typography>
          </Box>

          <LogoutIcon
            onClick={() => {
              logout();
            }}
            sx={{ cursor: 'pointer', color: 'text.secondary' }}
          />
        </AccountStyle>
      </Stack>

      <NavSection navConfig={menu} isShow={!isCollapse} />

      <Stack sx={{ px: 2.5, position: 'absolute', bottom: 20, width: '100%' }}>
        <Box
          onClick={getMedicalDeviceInfo}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: pxToRem(10),
            borderRadius: pxToRem(12),
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.grey[500], 0.12),
            width: '100%',
            cursor:'pointer'
          }}
        >
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              의료기기 라벨
            </Typography>
        </Box>

        <Box
          onClick={downloadManual}
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            p: pxToRem(10),
            borderRadius: pxToRem(12),
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.grey[500], 0.12),
            width: '100%',
            cursor:'pointer'
          }}
        >
            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
              사용자 매뉴얼
            </Typography>
        </Box>
      </Stack>
    </Scrollbar>
  );

  const dialog = (
    <>
      <CModalForm
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
        }}
        handleBackdropClick={() => {
          setModalOpen(!modalOpen);
        }}
        hasXButton={true}
        width={'45%'}
        title={'의료기기 표기 기재사항'}
        contents={
          <Box
            dangerouslySetInnerHTML={{
              __html: `${medicalDeviceInfoStore.medicalDeviceInfo.infoConts}`,
            }}
          ></Box>
        }
      />
    </>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <Drawer
        open={true}
        variant="persistent"
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: 'background.default',
            ...(isCollapse && {
              width: COLLAPSE_WIDTH,
            }),
            ...(collapseHover && {
              borderRight: 0,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
              boxShadow: (theme) => theme.customShadows.z20,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.88),
            }),
          },
        }}
      >
        {renderContent}
        {dialog}
      </Drawer>
    </RootStyle>
  );
});
